import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { LogOut, Mail, Crown, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { NAV_GROUPS, visibleNavItems, groupVisibleItems } from "./navConfig";

const Sidebar = () => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [kycActions, setKycActions] = useState(0);
  const isPrivileged = user?.role === "admin" || user?.role === "superuser";

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/messages/unread-count");
        if (!cancelled) setUnread(r.data?.unread || 0);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Admin badge — members awaiting a KYC / accreditation approval
  // (STARSEED-CAPMARKETS-071). Admin-only endpoint, so never fetched for
  // members; same filters as the Command Center tabs.
  useEffect(() => {
    if (!isPrivileged) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/command-center/admin/action-counts");
        if (!cancelled) setKycActions(r.data?.total || 0);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isPrivileged]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Role + module gating (unchanged semantics), then group into ordered,
  // non-empty sections. A badge count is injected for `badgeKey` items.
  const groups = useMemo(() => {
    const badges = { unread, kyc_actions: kycActions };
    const visible = visibleNavItems(user).map((item) =>
      item.badgeKey ? { ...item, badge: badges[item.badgeKey] || 0 } : item
    );
    return groupVisibleItems(visible);
  }, [user, unread, kycActions]);

  // Collapse scaffold (nav-ia-003): mechanism present, but all groups ship
  // expanded — no UX shift yet. Default-collapsed groups are Claude Design's
  // call in nav-ia-005, seeded from NAV_GROUPS[].defaultCollapsed.
  const [collapsed, setCollapsed] = useState(
    () => new Set(NAV_GROUPS.filter((g) => g.defaultCollapsed).map((g) => g.key))
  );
  const toggleGroup = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  return (
    <aside className="fixed left-0 top-0 h-screen w-64 bg-surface border-r border-platinum/10 flex flex-col z-50">
      {/* Logo */}
      <div className="p-6 border-b border-platinum/10">
        <Link to="/dashboard" className="flex items-center gap-4">
          <img src="/logo.png" alt="EchoSense Capital Club" className="w-14 h-14 object-contain" />
          <div>
            <h1 className="text-xl font-semibold text-foreground font-serif">EchoSense Capital Club</h1>
            <p className="text-xs text-muted-foreground capitalize">{user?.role} Portal</p>
          </div>
        </Link>
      </div>

      {/* Navigation — grouped, role-aware sections (nav-ia-003) */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {groups.map((group) => {
          const isCollapsed = collapsed.has(group.key);
          const panelId = `nav-group-panel-${group.key}`;
          return (
            <div key={group.key}>
              <button
                type="button"
                onClick={() => toggleGroup(group.key)}
                aria-expanded={!isCollapsed}
                aria-controls={panelId}
                data-testid={`nav-group-${group.key}`}
                className="w-full flex items-center justify-between px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700 transition-colors"
              >
                <span>{group.label}</span>
                {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              </button>
              {!isCollapsed && (
                <div id={panelId} role="group" aria-label={group.label} className="space-y-1">
                  {group.items.map((item) => {
                    const isActive = location.pathname === item.href;
                    const Icon = item.icon;
                    const slug = item.testId || item.label.toLowerCase().replace(/\s+/g, "-");
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        aria-current={isActive ? "page" : undefined}
                        data-testid={`nav-${slug}`}
                        className={cn(
                          "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                          isActive
                            ? "bg-blue-50 text-blue-700 border-l-2 border-blue-600 font-semibold"
                            : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                        )}
                      >
                        <Icon size={18} />
                        <span className="text-sm font-medium flex-1">{item.label}</span>
                        {item.badge > 0 && (
                          <span className="bg-blue-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center" data-testid={`nav-badge-${slug}`}>
                            {item.badge > 99 ? "99+" : item.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-platinum/10">
        <div className="mb-4 px-4">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-foreground truncate" data-testid="sidebar-user-name">{user?.full_name}</p>
            {user?.is_pioneer && user?.pioneer_number && (
              <span
                data-testid="sidebar-pioneer-badge"
                title={`Founding Pioneer №${String(user.pioneer_number).padStart(3, "0")} — locked-in Pioneer pricing & priority Deal Room access.`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tabular-nums uppercase tracking-wider whitespace-nowrap border border-amber-400/40 bg-gradient-to-r from-amber-400/15 to-amber-500/5 text-amber-300 shadow-[0_0_12px_rgba(234,179,8,0.25)]"
              >
                <Crown size={10} className="text-amber-300" />
                Pioneer №{String(user.pioneer_number).padStart(3, "0")}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate" data-testid="sidebar-user-email">{user?.email}</p>
          {user?.internal_email && (
            <p className="text-[11px] text-blue-700/80 truncate mt-1 flex items-center gap-1" data-testid="sidebar-internal-email" title="Your EchoSense Capital Club portal address">
              <Mail size={10} className="shrink-0" />
              <span className="font-mono">{user.internal_email}</span>
            </p>
          )}
        </div>
        <Button 
          variant="outline" 
          className="w-full justify-start gap-2 border-platinum/20 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/50"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut size={16} />
          Sign Out
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
